import { Action, combineReducers, configureStore, ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { getTreatments, initSplitSdk, splitReducer } from '@splitsoftware/splitio-redux';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import { api } from '../gql/generated/queries';
import { rtkQueryErrorLogger } from './errorMiddleware';
import snackSlice from './slices/snackSlice/snack';
import reportSlice from './slices/reportSlice/report';
import drawBudgetSlice from './slices/drawBudgetSlice/drawBudget';
import uploadSlice from './slices/uploadSlice/upload';
import { splitNames } from "../vendor/splitio";

const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  snacks: snackSlice,
  selectedReport: reportSlice,
  drawBudget: drawBudgetSlice,
  uploads: uploadSlice,
  splitio: splitReducer,
});

/**
 * Used for setting up tests
 * @param preloadedState
 */
export const setupStore = (preloadedState?: Partial<RootState>) => {
  return configureStore({
    reducer: rootReducer,
    preloadedState
  })
}

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types
        ignoredActions: ['upload/addFiles'],
        // Ignore these field paths in all actions
        // ignoredActionPaths: ['uploadSlice.addFiles'],
      },
    }).concat(api.middleware, rtkQueryErrorLogger),
  devTools: process.env.NODE_ENV !== 'production',
});

store.dispatch(initSplitSdk({
  config: {
    core: {
      authorizationKey: process.env.REACT_APP_SPLITIO_KEY || '',
      // key represents your internal user id, or the account id that the user belongs to.
      // This could also be a cookie you generate for anonymous users.
      key: 'key',
    },
  },
}));
store.dispatch(getTreatments({ splitNames, evalOnUpdate: true }) as UnknownAction);

export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;
export type RootState = ReturnType<typeof store.getState>;
export type RootReducerState = ReturnType<typeof rootReducer>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
